import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';

const PasswordRecovery = () => {
  const navigate = useNavigate();
  const { resetPassword } = useAuth();
  
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [canResend, setCanResend] = useState(false);
  const [countdown, setCountdown] = useState(0);

  React.useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (countdown === 0 && emailSent) {
      setCanResend(true);
    }
  }, [countdown, emailSent]);

  const validateEmail = () => {
    if (!email) {
      setError('Email address is required');
      return false;
    }
    
    if (!/\S+@\S+\.\S+/?.test(email)) {
      setError('Please enter a valid email address');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateEmail()) return;

    setIsLoading(true);
    setError('');

    try {
      const { error: resetError } = await resetPassword(email);
      
      if (resetError) {
        setError(resetError?.message || 'Failed to send recovery email. Please try again.');
      } else {
        setEmailSent(true);
        setCanResend(false);
        setCountdown(60); // 60 second cooldown before allowing resend
      }
    } catch (error) {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    if (!canResend || isLoading) return;
    
    setIsLoading(true);
    setError('');

    try {
      const { error: resetError } = await resetPassword(email);
      
      if (resetError) {
        setError(resetError?.message || 'Failed to resend recovery email. Please try again.');
      } else {
        setCanResend(false);
        setCountdown(60);
      }
    } catch (error) {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  if (emailSent) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        
        <div className="container mx-auto px-4 py-12 max-w-2xl">
          <div className="bg-card border border-border rounded-lg shadow-elevation-2 p-8 md:p-12">
            {/* Success Icon */}
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Icon name="MailCheck" size={40} className="text-success" />
              </div>
              
              <h1 className="text-3xl font-bold text-card-foreground mb-3">
                Recovery Email Sent
              </h1>
              <p className="text-lg text-muted-foreground">
                We've sent password reset instructions to
              </p>
              <p className="text-lg font-semibold text-primary mt-2">
                {email}
              </p>
            </div>

            {/* Instructions */}
            <div className="bg-muted/50 border border-border rounded-lg p-6 mb-8">
              <h3 className="text-sm font-semibold text-card-foreground mb-3 flex items-center">
                <Icon name="CheckCircle" size={18} className="mr-2 text-primary" />
                Next Steps
              </h3>
              <ol className="space-y-3 ml-6">
                <li className="text-sm text-muted-foreground flex items-start">
                  <span className="text-primary font-semibold mr-2">1.</span>
                  <span>Check your email inbox for our password reset message</span>
                </li>
                <li className="text-sm text-muted-foreground flex items-start">
                  <span className="text-primary font-semibold mr-2">2.</span>
                  <span>Click the secure reset link in the email (valid for 1 hour)</span>
                </li>
                <li className="text-sm text-muted-foreground flex items-start">
                  <span className="text-primary font-semibold mr-2">3.</span>
                  <span>Create your new password and return to sign in</span>
                </li>
              </ol>
            </div>

            <div className="bg-info/10 border border-info/20 rounded-lg p-4 mb-6">
              <div className="flex items-start space-x-3">
                <Icon name="Info" size={20} className="text-info flex-shrink-0 mt-0.5" />
                <div className="text-sm text-info-foreground">
                  <p className="font-semibold mb-1">Email not arriving?</p>
                  <ul className="space-y-1 text-xs">
                    <li>• Check your spam or junk folder</li>
                    <li>• Verify {email} is correct</li>
                    <li>• Allow 2-5 minutes for delivery</li>
                    <li>• Link expires after 1 hour for security</li>
                  </ul>
                </div>
              </div>
            </div>

            {error && (
              <div className="bg-error/10 border border-error/20 rounded-lg p-4 mb-6 flex items-center space-x-3">
                <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0" />
                <p className="text-sm text-error">{error}</p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="space-y-4">
              <Button
                onClick={handleResend}
                variant="outline"
                fullWidth
                disabled={!canResend || isLoading}
                loading={isLoading}
                iconName="RefreshCw"
                iconPosition="left"
              >
                {isLoading
                  ? 'Sending...'
                  : countdown > 0
                  ? `Resend Available in ${countdown}s`
                  : 'Resend Recovery Email'}
              </Button>

              <Button
                onClick={handleBackToLogin}
                variant="ghost"
                fullWidth
                iconName="ArrowLeft"
                iconPosition="left"
              >
                Back to Login
              </Button>
            </div>

            {/* Security Notice */}
            <div className="mt-8 pt-6 border-t border-border">
              <div className="flex items-start space-x-3">
                <Icon name="Shield" size={20} className="text-success flex-shrink-0 mt-0.5" />
                <div className="text-xs text-muted-foreground">
                  <p className="font-semibold text-card-foreground mb-1">Security Notice</p>
                  <p>For your protection, password reset links expire after 1 hour. If you didn't request this reset, you can safely ignore this email - your account remains secure.</p>
                </div>
              </div>
            </div>

            {/* Support */}
            <div className="mt-6 text-center">
              <p className="text-sm text-muted-foreground mb-2">
                Need additional help?
              </p>
              <button
                onClick={() => navigate('/contact')}
                className="text-sm text-primary hover:text-primary/80 transition-micro font-medium inline-flex items-center"
              >
                <Icon name="HelpCircle" size={16} className="mr-1" />
                Contact Support
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <div className="container mx-auto px-4 py-12 max-w-md">
        <div className="bg-card border border-border rounded-lg shadow-elevation-2 p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="KeyRound" size={32} className="text-primary" />
            </div>
            <h1 className="text-2xl font-bold text-card-foreground mb-2">
              Reset Your Password
            </h1>
            <p className="text-muted-foreground text-sm">
              Enter your email address and we'll send you instructions to reset your password
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-error/10 border border-error/20 rounded-lg p-4 flex items-center space-x-3">
                <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0" />
                <p className="text-sm text-error">{error}</p>
              </div>
            )}

            <Input
              label="Email Address"
              type="email"
              name="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => {
                setEmail(e?.target?.value);
                if (error) setError('');
              }}
              error={error && !email ? error : ''}
              required
              disabled={isLoading}
              autoComplete="email"
              autoFocus
            />

            <div className="bg-muted/50 border border-border rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <Icon name="Clock" size={18} className="text-muted-foreground flex-shrink-0 mt-0.5" />
                <div className="text-xs text-muted-foreground">
                  <p className="font-semibold text-card-foreground mb-1">Estimated Delivery</p>
                  <p>Password reset emails typically arrive within 2-5 minutes. Please check your spam folder if you don't see it.</p>
                </div>
              </div>
            </div>

            <Button
              type="submit"
              fullWidth
              loading={isLoading}
              iconName="Mail"
              iconPosition="left"
            >
              {isLoading ? 'Sending...' : 'Send Recovery Email'}
            </Button>

            <div className="pt-4 border-t border-border">
              <button
                type="button"
                onClick={handleBackToLogin}
                className="text-sm text-primary hover:text-primary/80 transition-micro font-medium w-full text-center inline-flex items-center justify-center"
                disabled={isLoading}
              >
                <Icon name="ArrowLeft" size={16} className="mr-1" />
                Back to Login
              </button>
            </div>
          </form>

          {/* Troubleshooting */}
          <div className="mt-8 pt-6 border-t border-border">
            <h4 className="text-sm font-semibold text-card-foreground mb-3 flex items-center">
              <Icon name="AlertCircle" size={16} className="mr-2 text-warning" />
              Common Issues
            </h4>
            <ul className="space-y-2 text-xs text-muted-foreground">
              <li className="flex items-start">
                <Icon name="Dot" size={16} className="mr-1 flex-shrink-0" />
                <span>Make sure you're using the email address registered with your account</span>
              </li>
              <li className="flex items-start">
                <Icon name="Dot" size={16} className="mr-1 flex-shrink-0" />
                <span>Reset links expire after 1 hour for security purposes</span>
              </li>
              <li className="flex items-start">
                <Icon name="Dot" size={16} className="mr-1 flex-shrink-0" />
                <span>Contact support if you continue experiencing issues</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PasswordRecovery;